import { Search } from "lucide-react";
import { useState } from "react";

import { Button } from "@/shared/ui/button";
import { Checkbox } from "@/shared/ui/checkbox";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import { Slider } from "@/shared/ui/slider";

import type { ProductNode } from "@/entities/product";

import type { MapLeadQuota } from "../../api/mapLeadService";
import type { CityGroup, DistrictNode } from "@/shared/lib/amap/districtService";
import type { SearchForm } from "../../model/useMapLead";
import { CityCombobox } from "@/shared/ui/CityCombobox";
import { usePersona } from "@/shared/brand/persona";

/** 行政区服务失败时的兜底城市(退回 P1 交互:静态城市 + 区域手打;搜索链路不断)。 */
const FALLBACK_CITY_OPTIONS = [
  "上海市",
  "北京市",
  "深圳市",
  "广州市",
  "杭州市",
  "苏州市",
  "南京市",
  "成都市",
  "武汉市",
  "重庆市",
];

/** 客户类型(V1.0 §5.1 示例集;后续可换系统行业分类)。 */
const INDUSTRY_OPTIONS = [
  "企业服务",
  "装修公司",
  "餐饮",
  "酒店",
  "教育培训",
  "汽车",
  "医疗",
  "零售",
  "软件公司",
  "制造业",
];

const RADIUS_STOPS = [1, 3, 5, 10, 20];

interface Props {
  form: SearchForm;
  patch: (p: Partial<SearchForm>) => void;
  /** 主推产品候选(已过 pickableProducts 口径);null/空 = 不渲染该下拉。 */
  products: ProductNode[] | null;
  quota: MapLeadQuota | null;
  /** 每次搜索的积分价(服务端价目表;null = 价表未到/取失败,不许编数,退「按次计费」)。 */
  searchPrice: number | null;
  loading: boolean;
  mapReady: boolean;
  hasCenter: boolean;
  onSearch: () => void;
  /** null = 行政区服务不可用 → 兜底交互;有值 = 真数据级联下拉。 */
  cityGroups: CityGroup[] | null;
  districts: DistrictNode[] | null;
  selCityAdcode: string;
  selDistrictAdcode: string;
  onSelectCity: (adcode: string) => void;
  onSelectDistrict: (adcode: string) => void;
}

export function SearchPanel({
  form,
  patch,
  products,
  quota,
  searchPrice,
  loading,
  mapReady,
  hasCenter,
  onSearch,
  cityGroups,
  districts,
  selCityAdcode,
  selDistrictAdcode,
  onSelectCity,
  onSelectDistrict,
}: Props) {
  const persona = usePersona();
  const cascade = cityGroups != null;
  const hasProductCandidates = products != null && products.length > 0;
  // 「自定义输入」是下拉里的一个哨兵选项(不对应真实 nodeCode),选中后展开自由输入框;
  // 用本地态记它,而不是靠 form.productText 是否非空判断 —— 否则刚选中、还没打字时输入框会先闪一下不见。
  const [customProductMode, setCustomProductMode] = useState(
    () => hasProductCandidates && !form.productNodeCode && !!form.productText
  );

  return (
    <div className="flex flex-col gap-4 rounded-lg border bg-card p-4">
      <div className="space-y-1.5">
        <Label className="text-xs text-muted-foreground">所在城市</Label>
        {cascade ? (
          <CityCombobox
            cityGroups={cityGroups}
            selCityAdcode={selCityAdcode}
            onSelectCity={onSelectCity}
          />
        ) : (
          <Select value={form.city} onValueChange={(v) => patch({ city: v })}>
            <SelectTrigger className="h-9">
              <SelectValue placeholder="选择城市" />
            </SelectTrigger>
            <SelectContent>
              {FALLBACK_CITY_OPTIONS.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs text-muted-foreground">所在区域</Label>
        {cascade ? (
          <Select
            value={selDistrictAdcode || "__all"}
            onValueChange={(v) => onSelectDistrict(v === "__all" ? "" : v)}
            disabled={!selCityAdcode}
          >
            <SelectTrigger className="h-9" data-testid="district-select">
              <SelectValue placeholder={selCityAdcode ? "全市" : "先选城市"} />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="__all">全市(不限区县)</SelectItem>
              {(districts ?? []).map((d) => (
                <SelectItem key={d.adcode} value={d.adcode}>
                  {d.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Input
            className="h-9"
            placeholder="如:浦东新区(可留空)"
            value={form.district}
            onChange={(e) => patch({ district: e.target.value })}
          />
        )}
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs text-muted-foreground">客户类型</Label>
        <Select
          value={form.industry || undefined}
          onValueChange={(v) => patch({ industry: v === "__none" ? "" : v })}
        >
          <SelectTrigger className="h-9">
            <SelectValue placeholder="选择行业(可不选)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none">不限行业</SelectItem>
            {INDUSTRY_OPTIONS.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {hasProductCandidates ? (
        <div className="space-y-1.5">
          <Label
            className="text-xs text-muted-foreground"
            title={`选定后,商机评分与${persona.title}分析将围绕该产品精准匹配`}
          >
            主推产品
          </Label>
          <Select
            value={customProductMode ? "__custom" : form.productNodeCode || undefined}
            onValueChange={(v) => {
              if (v === "__none") {
                setCustomProductMode(false);
                patch({ productNodeCode: "", productText: "" });
                return;
              }
              if (v === "__custom") {
                setCustomProductMode(true);
                patch({ productNodeCode: "" });
                return;
              }
              setCustomProductMode(false);
              patch({ productNodeCode: v, productText: "" });
            }}
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder={`选填,不选则由${persona.title}自行判断`} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none">不指定</SelectItem>
              {products.map((p) => (
                <SelectItem key={p.code} value={p.code}>
                  {p.name}
                </SelectItem>
              ))}
              <SelectItem value="__custom">自定义输入…</SelectItem>
            </SelectContent>
          </Select>
          {customProductMode && (
            <Input
              className="h-9"
              placeholder="手填主推产品名称"
              value={form.productText}
              onChange={(e) => patch({ productText: e.target.value, productNodeCode: "" })}
              data-testid="map-lead-product-custom-input"
            />
          )}
        </div>
      ) : (
        <div className="space-y-1.5">
          <Label
            className="text-xs text-muted-foreground"
            title={`填了才会并入商机评分与${persona.title}分析;未建产品库也能用`}
          >
            主推产品
          </Label>
          <Input
            className="h-9"
            placeholder="选填,如:工业润滑油(未建产品库可直接填写)"
            value={form.productText}
            onChange={(e) => patch({ productText: e.target.value, productNodeCode: "" })}
            data-testid="map-lead-product-text"
          />
        </div>
      )}

      <div className="space-y-1.5">
        <Label className="text-xs text-muted-foreground">搜索方式</Label>
        <div className="flex gap-1 rounded-md border p-0.5">
          <button
            type="button"
            onClick={() => patch({ mode: "KEYWORD" })}
            className={`flex-1 rounded px-2 py-1.5 text-xs font-medium transition-colors ${
              form.mode === "KEYWORD"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-accent"
            }`}
          >
            关键词搜索
          </button>
          <button
            type="button"
            onClick={() => patch({ mode: "AROUND" })}
            disabled={!mapReady}
            title={mapReady ? undefined : "周边搜索需要地图就绪(高德 Key 未配置)"}
            className={`flex-1 rounded px-2 py-1.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
              form.mode === "AROUND"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-accent"
            }`}
          >
            周边搜索
          </button>
        </div>
        {form.mode === "AROUND" && (
          <p className="text-2xs leading-relaxed text-muted-foreground">
            {hasCenter
              ? "中心点已就位(选行政区自动定位,点地图可微调)"
              : "在地图上点一下,设为搜索中心点"}
          </p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs text-muted-foreground">关键词</Label>
        <Input
          className="h-9"
          placeholder="如:装修公司"
          value={form.keyword}
          onChange={(e) => patch({ keyword: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === "Enter") onSearch();
          }}
        />
      </div>

      {form.mode === "AROUND" && (
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">搜索半径 · {form.radiusKm}km</Label>
          <Slider
            min={0}
            max={RADIUS_STOPS.length - 1}
            step={1}
            value={[RADIUS_STOPS.indexOf(form.radiusKm)]}
            onValueChange={([i]) => patch({ radiusKm: RADIUS_STOPS[i] ?? 5 })}
          />
          <div className="flex justify-between text-2xs tabular-nums text-muted-foreground">
            {RADIUS_STOPS.map((s) => (
              <span key={s}>{s}km</span>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">更多筛选</Label>
        <label htmlFor="map-lead-only-with-phone" className="flex cursor-pointer items-center gap-2 text-xs">
          <Checkbox
            id="map-lead-only-with-phone"
            checked={form.onlyWithPhone}
            onCheckedChange={(v) => patch({ onlyWithPhone: v === true })}
          />
          只看有联系电话
        </label>
        <label htmlFor="map-lead-only-high-score" className="flex cursor-pointer items-center gap-2 text-xs">
          <Checkbox
            id="map-lead-only-high-score"
            checked={form.onlyHighScore}
            onCheckedChange={(v) => patch({ onlyHighScore: v === true })}
          />
          只看评分 ≥ 70
        </label>
      </div>

      <Button onClick={onSearch} disabled={loading} className="w-full">
        <Search className="mr-1.5 h-4 w-4" />
        {loading
          ? "正在搜索…"
          : searchPrice != null
            ? `开始搜索 · ${searchPrice} 积分/次`
            : "开始搜索 · 按次计费"}
      </Button>

      {quota && (
        <p className="text-center text-2xs tabular-nums text-muted-foreground">
          今日剩余搜索次数 {quota.remaining} / {quota.limit}
        </p>
      )}
    </div>
  );
}
